import { type MutableRefObject, useEffect, useRef } from 'react';
import { useBoolean } from 'ahooks';

/**
 * @name useDropFiles
 * @description only listen external file drag and paste
 */
export function useDropFiles({
  target,
  onFiles,
  disabled = false,
}: {
  target: MutableRefObject<Element | null>;
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}) {
  const [isDragging, { setTrue, setFalse }] = useBoolean(false);
  const onFilesRef = useRef(onFiles);
  const dragCounterRef = useRef(0);
  const isMountedRef = useRef(true);
  const hasExternalDragRef = useRef(false);

  useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  useEffect(() => {
    const element = target.current;
    if (!element || disabled) return;

    isMountedRef.current = true;

    const isExternalFileDrag = (e: DragEvent): boolean => {
      const dataTransfer = e.dataTransfer;
      if (!dataTransfer || !dataTransfer.types) return false;

      const types = Array.from(dataTransfer.types);
      return types.includes('Files');
    };

    const resetDragState = () => {
      if (!isMountedRef.current) return;
      dragCounterRef.current = 0;
      hasExternalDragRef.current = false;
      setFalse();
    };

    const handleDragEnd = () => {
      resetDragState();
      if (hasExternalDragRef.current) {
        hasExternalDragRef.current = false;
        document.removeEventListener('dragend', handleDragEnd);
      }
    };

    const addDragEndListener = () => {
      if (!hasExternalDragRef.current) {
        hasExternalDragRef.current = true;
        document.addEventListener('dragend', handleDragEnd);
      }
    };

    const removeDragEndListener = () => {
      if (hasExternalDragRef.current) {
        hasExternalDragRef.current = false;
        document.removeEventListener('dragend', handleDragEnd);
      }
    };

    const handleDragEnter = (e: Event) => {
      const dragEvent = e as DragEvent;
      if (!isExternalFileDrag(dragEvent)) return;

      dragEvent.preventDefault();
      dragEvent.stopPropagation();

      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) {
        setTrue();
        addDragEndListener();
      }
    };

    const handleDragOver = (e: Event) => {
      const dragEvent = e as DragEvent;
      if (!isExternalFileDrag(dragEvent)) return;

      dragEvent.preventDefault();
      dragEvent.stopPropagation();
    };

    const handleDragLeave = (e: Event) => {
      const dragEvent = e as DragEvent;
      const relatedTarget = dragEvent.relatedTarget as Node | null;
      const currentElement = target.current;

      if (!currentElement) {
        resetDragState();
        return;
      }

      if (relatedTarget === null) {
        resetDragState();
        removeDragEndListener();
        return;
      }

      if (!isExternalFileDrag(dragEvent)) return;

      dragEvent.preventDefault();
      dragEvent.stopPropagation();

      if (!currentElement.contains(relatedTarget)) {
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
          setFalse();
          removeDragEndListener();
        }
      }
    };

    const handleDrop = (e: Event) => {
      const dragEvent = e as DragEvent;
      dragEvent.preventDefault();
      dragEvent.stopPropagation();

      resetDragState();
      removeDragEndListener();

      if (isExternalFileDrag(dragEvent) && dragEvent.dataTransfer?.files?.length) {
        onFilesRef.current(Array.from(dragEvent.dataTransfer.files));
      }
    };

    const handlePaste = (e: Event) => {
      const pasteEvent = e as ClipboardEvent;
      const clipboardData = pasteEvent.clipboardData;

      if (!clipboardData?.files?.length) return;

      const currentElement = target.current;
      if (!currentElement) return;

      const targetElement = pasteEvent.target as Node | null;
      const activeElement = document.activeElement as Node | null;

      const isTargetInElement = targetElement && currentElement.contains(targetElement);
      const isActiveInElement = activeElement && currentElement.contains(activeElement);

      if (!isTargetInElement && !isActiveInElement) return;

      pasteEvent.preventDefault();
      pasteEvent.stopPropagation();

      const files = Array.from(clipboardData.files);
      if (files.length > 0) {
        onFilesRef.current(files);
      }
    };

    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('drop', handleDrop);
    document.addEventListener('paste', handlePaste);

    return () => {
      isMountedRef.current = false;
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('drop', handleDrop);
      document.removeEventListener('paste', handlePaste);
      removeDragEndListener();
      resetDragState();
    };
  }, [target, disabled, setTrue, setFalse]);

  // When disabled: isDragging is false, no listeners are bound, no data is accepted
  return { isDragging: disabled ? false : isDragging };
}
